import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, IconButton, Chip, InputAdornment, FormControl, InputLabel, Grid, Card, CardContent, Switch, Alert, Snackbar, useTheme, useMediaQuery, Divider } from '@mui/material';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import LightbulbCircleIcon from '@mui/icons-material/LightbulbCircle';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloseIcon from '@mui/icons-material/Close';

// 🔥 કેટેગરી વાઈઝ પરમિશન્સ
const permissionCategories = {
    "System Actions": { systemBackup: 'Download Backup' },
    "Analytics Dashboard": { analyticsSaveReview: 'Save Reviews', analyticsDeleteReview: 'Delete Reviews' },
    "Fund Selector": { uploadAum: 'Upload AUM', fullSync: 'Full Sync', manageFormulas: 'Manage Formulas', top5Fund: 'Top 5 Funds' },
    "Financial Planner": { plannerAddClient: 'Add Client', plannerGenerate: 'Generate AI Plan', plannerRules: 'Rules Book', plannerSave: 'Save to History', plannerViewHistory: 'View History', plannerExport: 'Export as PDF', plannerClear: 'Clear Plan', plannerDelete: 'Delete History/Versions' },
    "Recommended (Registered)": {
        recRegAddClient: 'Add Client Button',
        recRegEdit: 'Table Action Column (Edit)',
        recRegDelete: 'Action: Delete Selected',
        recRegDownloadTemplate: 'Action: Download Template',
        recRegUploadExcel: 'Action: Upload Excel',
        recRegExportExcel: 'Action: Export to Excel',
        recRegPdf: 'Report: Download PDF',
        recRegWa: 'Report: Share via WhatsApp'
    },
    "Recommended (Sending)": {
        recSendAdd: 'Add Recommendation Button',
        recSendEdit: 'Table Action Column (Edit)',
        recSendMove: 'Action: Move to Registered',
        recSendDelete: 'Action: Delete Selected',
        recSendDownloadTemplate: 'Action: Download Template',
        recSendUploadExcel: 'Action: Upload Excel',
        recSendExportExcel: 'Action: Export to Excel',
        recSendPdf: 'Report: Download PDF',
        recSendWa: 'Report: Share via WhatsApp'
    },
    "SIP Reminder": {
        sipAddSip: 'Add SIP Button',
        sipAddNote: 'Add Mobile Note',
        sipFilterStatus: 'Filter: All Status',
        sipFilterUrgency: 'Filter: All Time',
        sipStatus: 'Status Column (Enable/Disable)',
        sipActionCol: 'Action Column',
        sipLogCol: 'History Log Column',
        sipEditCol: 'Edit Column',
        sipUndo: 'Undo Action',
        sipActionProcess: 'Action: Process Cycle',
        sipActionDelete: 'Action: Delete Selected',
        sipActionDownload: 'Action: Download Template',
        sipActionUpload: 'Action: Upload Excel',
        sipActionExport: 'Action: Export Active',
        sipActionViewHistory: 'Action: View History',
        sipActionExportHistory: 'Action: Export History',
        sipReportPdf: 'Report: Download PDF',
        sipReportWa: 'Report: WhatsApp Share'
    }
};

const UserManagement = () => {
    const { user: currentUser, refreshUser } = useContext(AuthContext);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [users, setUsers] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [showPassword, setShowPassword] = useState({});

    const [globalRoles, setGlobalRoles] = useState({});
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [selectedRoleEdit, setSelectedRoleEdit] = useState('Team');

    const [editData, setEditData] = useState({ id: '', name: '', email: '', role: '', status: '', password: '' });
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });

    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'Team' });

    const token = localStorage.getItem('token');
    const canManage = currentUser?.role === 'System Owner' || currentUser?.role === 'Administrator';
    const isOwner = currentUser?.role === 'System Owner';

    const fetchUsers = async () => {
        try {
            const res = await axios.get('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } });
            let fetchedUsers = res.data;
            if (currentUser?.role === 'Administrator') fetchedUsers = fetchedUsers.filter(u => u.role !== 'System Owner');
            setUsers(fetchedUsers);
        } catch (error) { console.error("Access Denied"); }
    };

    const fetchRoles = async () => {
        try {
            const res = await axios.get('/api/auth/roles', { headers: { Authorization: `Bearer ${token}` } });
            setGlobalRoles(res.data);
        } catch (error) { console.error("Error fetching roles"); }
    };

    useEffect(() => {
        fetchUsers();
        if (canManage) fetchRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveRolePermissions = async () => {
        try {
            await axios.put('/api/auth/roles', { roleName: selectedRoleEdit, permissions: globalRoles[selectedRoleEdit] || {} }, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, msg: "✅ Role Permissions Updated Globally!", type: 'success' });
            setOpenRoleDialog(false);
            fetchUsers();
            if (refreshUser) refreshUser();
        } catch (error) { setSnackbar({ open: true, msg: "Error updating role", type: 'error' }); }
    };

    const handleAddUser = async () => {
        try {
            await axios.post('/api/auth/user', formData, { headers: { Authorization: `Bearer ${token}` } });
            setOpenAddDialog(false);
            setFormData({ name: '', email: '', password: '', role: 'Team' });
            setSnackbar({ open: true, msg: "✅ User created successfully!", type: 'success' });
            fetchUsers();
        } catch (error) { setSnackbar({ open: true, msg: error.response?.data?.message || 'Error adding user', type: 'error' }); }
    };

    const handleEditClick = (user) => {
        setEditData({
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role,
            status: user.status, // આ લાઈન નવી ઉમેરો
            password: ''
        });
        setOpenEditDialog(true);
    };

    const handleUpdateUser = async () => {
        try {
            await axios.put(`/api/auth/user/${editData.id}`, editData, { headers: { Authorization: `Bearer ${token}` } });
            setOpenEditDialog(false);
            setSnackbar({ open: true, msg: "✅ User details saved successfully!", type: 'success' });
            fetchUsers();
            if (refreshUser) refreshUser();
        } catch (error) { setSnackbar({ open: true, msg: error.response?.data?.message || "Error updating user", type: 'error' }); }
    };

    const handleDeleteUser = async (id) => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            try {
                await axios.delete(`/api/auth/user/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                setSnackbar({ open: true, msg: "✅ User deleted successfully!", type: 'success' });
                fetchUsers();
            } catch (error) { setSnackbar({ open: true, msg: "Error deleting user", type: 'error' }); }
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
            await axios.put(`/api/auth/user/${id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
            fetchUsers();
        } catch (error) { setSnackbar({ open: true, msg: "Error changing status", type: 'error' }); }
    };

    return (
        <Box p={{ xs: 1.5, md: 3 }} sx={{
            pb: { xs: 10, md: 3 },
            height: { xs: 'calc(100vh - 65px)', md: 'calc(100vh - 85px)' },
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
        }}>
            {/* ========================================== */}
            {/* 🖥️ DESKTOP HEADER                          */}
            {/* ========================================== */}
            <Box display={{ xs: 'none', md: 'flex' }} justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold" color="#204f4f">User Management</Typography>
                <Box>
                    {isOwner && (
                        <Button variant="outlined" sx={{ color: '#204f4f', borderColor: '#204f4f', fontWeight: 'bold', mr: 2, '&:hover': { bgcolor: '#fff3e0', borderColor: '#204f4f' } }} onClick={() => setOpenRoleDialog(true)} startIcon={<SettingsSuggestIcon />}>
                            Configure Role Access
                        </Button>
                    )}
                    {canManage && (
                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: '#204f4f',
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: '#163838' }
                            }}
                            onClick={() => setOpenAddDialog(true)}
                            startIcon={<PersonAddRoundedIcon />}
                        >
                            Add New User
                        </Button>
                    )}
                </Box>
            </Box>

            {/* ========================================== */}
            {/* 📱 MOBILE HEADER                           */}
            {/* ========================================== */}
            <Box display={{ xs: 'block', md: 'none' }} mb={2} sx={{ width: '100%' }}>
                <Typography variant="h5" fontWeight="bold" color="#204f4f" mb={1.5}>
                    User Management
                </Typography>

                {isOwner && (
                    <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                            width: '100% !important',
                            ml: '0 !important',
                            mr: '0 !important',
                            mb: '12px !important',
                            color: '#204f4f',
                            borderColor: '#204f4f',
                            fontWeight: 'bold',
                            boxSizing: 'border-box',
                            '&:hover': { bgcolor: '#fff3e0', borderColor: '#204f4f' }
                        }}
                        onClick={() => setOpenRoleDialog(true)}
                        startIcon={<SettingsSuggestIcon />}
                    >
                        Configure Role Access
                    </Button>
                )}

                {canManage && (
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{
                            width: '100% !important',
                            ml: '0 !important',
                            mr: '0 !important',
                            bgcolor: '#204f4f',
                            color: 'white',
                            fontWeight: 'bold',
                            boxSizing: 'border-box',
                            '&:hover': { bgcolor: '#163838' }
                        }}
                        onClick={() => setOpenAddDialog(true)}
                        startIcon={<PersonAddRoundedIcon />}
                    >
                        Add New User
                    </Button>
                )}
            </Box>

            {/* ========================================== */}
            {/* 🖥️ DESKTOP TABLE                           */}
            {/* ========================================== */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#1e4d4d' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#ffffff' }}>Password</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ffffff' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id} hover>
                                    <TableCell sx={{ fontWeight: '600', color: '#333' }}>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Chip label={user.role} size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: user.role === 'System Owner' ? '#e3f2fd' : user.role === 'Administrator' ? '#fff3e0' : user.role === 'Follow Up Team' ? '#f3e5f5' : '#f1f8e9',
                                                color: user.role === 'System Owner' ? '#1565c0' : user.role === 'Administrator' ? '#e65100' : user.role === 'Follow Up Team' ? '#8e24aa' : '#33691e'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={user.status} size="small" onClick={() => { if (canManage && user.role !== 'System Owner') handleToggleStatus(user.id, user.status) }}
                                            sx={{ fontWeight: 'bold', cursor: (canManage && user.role !== 'System Owner') ? 'pointer' : 'default', bgcolor: user.status === 'Active' ? '#e8f5e9' : '#ffebee', color: user.status === 'Active' ? '#2e7d32' : '#c62828' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: '#555', fontFamily: 'monospace' }}>
                                        <Box display="flex" alignItems="center">
                                            <Box sx={{ letterSpacing: showPassword[user.id] ? 'normal' : '2px', minWidth: '80px' }}>{showPassword[user.id] ? (user.rawPassword || "Hidden") : "••••••••"}</Box>
                                            <IconButton onClick={() => setShowPassword(prev => ({ ...prev, [user.id]: !prev[user.id] }))} size="small" sx={{ ml: 1 }}>
                                                {showPassword[user.id] ? <VisibilityOffRoundedIcon fontSize="small" sx={{ color: '#1e4d4d' }} /> : <VisibilityRoundedIcon fontSize="small" sx={{ color: '#999' }} />}
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {((canManage && user.role !== 'System Owner') || (isOwner && user.role === 'System Owner')) && (
                                            <IconButton onClick={() => handleEditClick(user)} size="small" sx={{ color: '#1976d2', bgcolor: '#e3f2fd', mr: 1 }}><EditRoundedIcon fontSize="small" /></IconButton>
                                        )}
                                        {canManage && user.role !== 'System Owner' && (
                                            <IconButton onClick={() => handleDeleteUser(user.id)} size="small" sx={{ color: '#d32f2f', bgcolor: '#ffebee' }}><DeleteRoundedIcon fontSize="small" /></IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* ========================================== */}
            {/* 📱 MOBILE CARDS VIEW                       */}
            {/* ========================================== */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, pb: 2 }}>
                {users.map((user) => (
                    <Card key={user.id} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#333" lineHeight={1.2}>{user.name}</Typography>
                                    <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                        <EmailIcon sx={{ fontSize: '0.9rem' }} /> {user.email}
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                    {((canManage && user.role !== 'System Owner') || (isOwner && user.role === 'System Owner')) && (
                                        <IconButton onClick={() => handleEditClick(user)} size="small" sx={{ color: '#1976d2', bgcolor: '#e3f2fd', padding: '6px' }}><EditRoundedIcon fontSize="small" /></IconButton>
                                    )}
                                    {canManage && user.role !== 'System Owner' && (
                                        <IconButton onClick={() => handleDeleteUser(user.id)} size="small" sx={{ color: '#d32f2f', bgcolor: '#ffebee', padding: '6px' }}><DeleteRoundedIcon fontSize="small" /></IconButton>
                                    )}
                                </Box>
                            </Box>

                            <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Chip label={user.role} size="small"
                                    sx={{
                                        fontWeight: 'bold', fontSize: '0.75rem',
                                        bgcolor: user.role === 'System Owner' ? '#e3f2fd' : user.role === 'Administrator' ? '#fff3e0' : user.role === 'Follow Up Team' ? '#f3e5f5' : '#f1f8e9',
                                        color: user.role === 'System Owner' ? '#1565c0' : user.role === 'Administrator' ? '#e65100' : user.role === 'Follow Up Team' ? '#8e24aa' : '#33691e'
                                    }}
                                />
                                <Chip label={user.status} size="small" onClick={() => { if (canManage && user.role !== 'System Owner') handleToggleStatus(user.id, user.status) }}
                                    sx={{ fontWeight: 'bold', fontSize: '0.75rem', cursor: (canManage && user.role !== 'System Owner') ? 'pointer' : 'default', bgcolor: user.status === 'Active' ? '#e8f5e9' : '#ffebee', color: user.status === 'Active' ? '#2e7d32' : '#c62828' }}
                                />
                            </Box>

                            <Box display="flex" alignItems="center" justifyContent="space-between" bgcolor="#f5f5f5" p={1} px={1.5} borderRadius={1}>
                                <Typography variant="caption" fontWeight="bold" color="#777">Password:</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Box sx={{ letterSpacing: showPassword[user.id] ? 'normal' : '2px', fontFamily: 'monospace', color: '#333', fontSize: '0.85rem' }}>
                                        {showPassword[user.id] ? (user.rawPassword || "Hidden") : "••••••••"}
                                    </Box>
                                    <IconButton onClick={() => setShowPassword(prev => ({ ...prev, [user.id]: !prev[user.id] }))} size="small" sx={{ p: 0.5 }}>
                                        {showPassword[user.id] ? <VisibilityOffRoundedIcon fontSize="small" sx={{ color: '#1e4d4d' }} /> : <VisibilityRoundedIcon fontSize="small" sx={{ color: '#999' }} />}
                                    </IconButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Box sx={{ minHeight: { xs: '80px', md: '40px' }, width: '100%', flexShrink: 0 }} />

            {/* ========================================== */}
            {/* DIALOGS                                    */}
            {/* ========================================== */}

            <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="xl" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: { xs: 0, md: 3 } } }}>
                <DialogTitle sx={{ backgroundColor: '#fdfbf7', color: '#1e4d4d', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 }, borderBottom: '1px solid #eee' }}>
                    <Box display="flex" alignItems="center"><SettingsSuggestIcon sx={{ mr: { xs: 1, md: 1.5 }, fontSize: { xs: 24, md: 28 } }} /> Configure Global Roles</Box>
                    <IconButton onClick={() => setOpenRoleDialog(false)}><CloseIcon /></IconButton>
                </DialogTitle>

                {/* 🔥 અહીં સ્કોલબાર હાઇડ કરવાનો કોડ ઉમેર્યો છે */}
                <DialogContent sx={{
                    p: { xs: 2, md: 4 },
                    backgroundColor: '#fafafa',
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                }}>
                    <Alert
                        icon={<LightbulbCircleIcon fontSize="inherit" />}
                        sx={{ mt: { xs: 1, md: 2 }, mb: 3, borderRadius: 2, bgcolor: 'rgba(30, 77, 77, 0.1)', color: '#1e4d4d', '& .MuiAlert-icon': { color: '#1e4d4d' }, '& .MuiAlert-message': { fontWeight: '500', fontSize: { xs: '0.8rem', md: '1rem' } } }}
                    >
                        Changes made here will instantly apply to ALL existing and future users who have this role assigned.
                    </Alert>

                    <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, borderLeft: '4px solid #c4a161' }}>
                        <CardContent sx={{ p: { xs: 2, md: 2 } }}>
                            <Typography variant="h6" sx={{ color: '#c4a161', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                <AssignmentIndIcon sx={{ mr: 1 }} /> Select Role to Edit
                            </Typography>
                            <FormControl fullWidth={isMobile} sx={{ minWidth: { xs: '100%', md: 300 } }}>
                                <Select value={selectedRoleEdit} onChange={e => setSelectedRoleEdit(e.target.value)} sx={{ bgcolor: '#fff' }} size={isMobile ? "small" : "medium"}>
                                    <MenuItem value="Administrator">Administrator (Full Access)</MenuItem>
                                    <MenuItem value="Follow Up Team">Follow Up Team (SIP Admin)</MenuItem>
                                    <MenuItem value="Team">Team (Limited Access)</MenuItem>
                                </Select>
                            </FormControl>
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid #1e4d4d' }}>
                        <CardContent sx={{ p: { xs: 2, md: 2 } }}>
                            <Typography variant="h6" sx={{ color: '#1e4d4d', fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', md: '1.25rem' }, flexWrap: 'wrap' }}>
                                <LockIcon sx={{ mr: 1 }} /> Access Map for '{selectedRoleEdit}'
                            </Typography>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
                                {Object.keys(permissionCategories).map(categoryName => {
                                    const categoryKeys = Object.keys(permissionCategories[categoryName]);
                                    const isAllChecked = categoryKeys.length > 0 && categoryKeys.every(key => globalRoles[selectedRoleEdit]?.[key]);

                                    return (
                                        <Box key={categoryName} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fdfbf7', transition: '0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' } }}>
                                            {/* Master Toggle Box */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #ddd', pb: 0.5, mb: 1.5 }}>
                                                <Typography variant="subtitle2" fontWeight="900" sx={{ color: '#204f4f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {categoryName}
                                                </Typography>
                                                <Switch
                                                    size="small"
                                                    checked={isAllChecked}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        const updatedRolePerms = { ...(globalRoles[selectedRoleEdit] || {}) };
                                                        categoryKeys.forEach(key => {
                                                            updatedRolePerms[key] = checked;
                                                        });
                                                        setGlobalRoles({
                                                            ...globalRoles,
                                                            [selectedRoleEdit]: updatedRolePerms
                                                        });
                                                    }}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#204f4f' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#204f4f' },
                                                        mr: -1
                                                    }}
                                                />
                                            </Box>

                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                                                {categoryKeys.map(key => (
                                                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: '#f4f4f4' } }}>
                                                        <Typography variant="body2" fontWeight="600" color="#555" sx={{ fontSize: '0.75rem', width: '80%' }}>{permissionCategories[categoryName][key]}</Typography>
                                                        <Switch
                                                            size="small"
                                                            checked={globalRoles[selectedRoleEdit]?.[key] || false}
                                                            onChange={(e) => setGlobalRoles({
                                                                ...globalRoles,
                                                                [selectedRoleEdit]: { ...globalRoles[selectedRoleEdit], [key]: e.target.checked }
                                                            })}
                                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1e4d4d' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1e4d4d' } }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ minHeight: { xs: '40px', md: 0 }, width: '100%', flexShrink: 0 }} />
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#fdfbf7', borderTop: '1px solid #eee', flexWrap: 'wrap', gap: { xs: 1, md: 0 } }}>
                    <Button onClick={() => setOpenRoleDialog(false)} sx={{ color: '#555', fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#1e4d4d', color: '#ffffff', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#163838' }, width: { xs: '100%', sm: 'auto' } }} onClick={handleSaveRolePermissions}>Apply & Save Globally</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: { xs: 0, md: 3 } } }}>
                <DialogTitle sx={{ backgroundColor: '#fdfbf7', color: '#204f4f', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 }, borderBottom: '1px solid #eee' }}>
                    Create New Account
                    <IconButton onClick={() => setOpenAddDialog(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#fafafa' }}>
                    <Alert severity="success" icon={false} sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 3, md: 4 }, borderRadius: 2, bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: { xs: '0.85rem', md: '1rem' } }}>
                        💡 <b>Tip:</b> Permissions are automatically assigned based on the selected Role.
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                        <TextField fullWidth label="Full Name" size="small" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} autoComplete="off" InputProps={{ startAdornment: (<InputAdornment position="start"><AccountCircleIcon color="action" /></InputAdornment>) }} />
                        <TextField fullWidth label="Email Address" size="small" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} autoComplete="off" InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>) }} />
                        <TextField fullWidth label="Password" size="small" type={showPassword['add'] ? 'text' : 'password'} value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} autoComplete="new-password" InputProps={{ startAdornment: (<InputAdornment position="start"><LockIcon color="action" /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(prev => ({ ...prev, add: !prev.add }))} edge="end">{showPassword['add'] ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}</IconButton></InputAdornment>) }} />

                        <FormControl fullWidth>
                            <InputLabel size="small">Select Role</InputLabel>
                            <Select value={formData.role} label="Select Role" size="small" onChange={e => setFormData({ ...formData, role: e.target.value })}>
                                <MenuItem value="Administrator">Administrator (Full Access)</MenuItem>
                                <MenuItem value="Follow Up Team">Follow Up Team (SIP Admin)</MenuItem>
                                <MenuItem value="Team">Team (Limited Access)</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#fdfbf7', borderTop: '1px solid #eee', flexWrap: 'wrap', gap: { xs: 1, md: 0 } }}>
                    <Button onClick={() => setOpenAddDialog(false)} color="inherit" sx={{ fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#204f4f', color: 'white', fontWeight: 'bold', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#163838' }, width: { xs: '100%', sm: 'auto' } }} onClick={handleAddUser}>Create User</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: { xs: 0, md: 3 } } }}>
                <DialogTitle sx={{ backgroundColor: '#fdfbf7', color: '#204f4f', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 }, borderBottom: '1px solid #eee' }}>
                    Edit User Details
                    <IconButton onClick={() => setOpenEditDialog(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', mt: { xs: 1, md: 2 } }}>
                        <TextField fullWidth label="Full Name" variant="outlined" size="small" value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} />

                        <TextField fullWidth label="User ID (Email Address)" variant="outlined" size="small" value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} />

                        <FormControl fullWidth>
                            <InputLabel size="small">Role</InputLabel>
                            <Select size="small" value={editData.role} label="Role" onChange={e => setEditData({ ...editData, role: e.target.value })} disabled={editData.role === 'System Owner'}>
                                {editData.role === 'System Owner' && <MenuItem value="System Owner">System Owner</MenuItem>}
                                <MenuItem value="Administrator">Administrator (Full Access)</MenuItem>
                                <MenuItem value="Follow Up Team">Follow Up Team (SIP Admin)</MenuItem>
                                <MenuItem value="Team">Team (Limited Access)</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth label="New Password (Optional)" size="small" type={showPassword['edit'] ? 'text' : 'password'} variant="outlined"
                            value={editData.password} onChange={e => setEditData({ ...editData, password: e.target.value })}
                            InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(prev => ({ ...prev, edit: !prev.edit }))} edge="end">{showPassword['edit'] ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}</IconButton></InputAdornment>) }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#fdfbf7', borderTop: '1px solid #eee', flexWrap: 'wrap', gap: { xs: 1, md: 0 } }}>
                    <Button onClick={() => setOpenEditDialog(false)} color="inherit" sx={{ fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#204f4f', color: 'white', fontWeight: 'bold', borderRadius: 2, px: 4, '&:hover': { bgcolor: '#163838' }, width: { xs: '100%', sm: 'auto' } }} onClick={handleUpdateUser}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.type} sx={{ width: '100%', fontWeight: 'bold' }}>{snackbar.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default UserManagement;